import { registerComponent, registerSystem } from "aframe";
import { Matrix4, Vector3 } from "three";
import { transformPoint, transformVector } from "../../three/math";
import { SetAnimationEventDetail } from "../avatar";
import { GetRayCallback, InputComponent, InputSystem, inputSystemSchema, press, release } from "./common";



interface XrInputSystem extends InputSystem
{
	instances : { [ attrName:string ] : XrInputComponent };
};
interface XrInputComponent extends InputComponent
{
	data : { [action:string] : string }; //Specialized actions
	system : XrInputSystem;
	input? : XRInputSource;
	rayOrigin : Vector3;
	rayDirection : Vector3;
	getRay : GetRayCallback;
	buttons : number[];
	axes : number[];
	gesture : string;
	updateButtons( buttons : ReadonlyArray<GamepadButton> ) : XrInputComponent;
	updateAxes( axes : ReadonlyArray<number> ) : XrInputComponent;
	updateGesture( hand : XRHand, frame : XRFrame, ref : XRReferenceSpace ) : XrInputComponent;
	updateRay( pose : XRPose | undefined, globalOffset : Matrix4 ) : XrInputComponent;
}

const baseButtonNames = [ "Trigger", "Grip", "Pad", "Stick" ];
const baseAxisNames = [ "Pad", "Stick" ];
const buttonName = (index : number) =>
	baseButtonNames[index] || String.fromCharCode("A".charCodeAt(0) + index - baseButtonNames.length);
const axisName = (index : number, direction : number) =>
{
	const base = baseAxisNames[ Math.floor(index / 2) ];
	return base + ((index % 2) ? ((direction > 0) ? "Down" : "Up") : ((direction > 0) ? "Right" : "Left"));
};





registerSystem("xr-input", {
	schema : inputSystemSchema,
	init(this : XrInputSystem) {
		this.instances = {};
	},
	tick(this : XrInputSystem, t:number, dt:number)
	{
		const xr = this.el.renderer.xr;
		if(!(xr && xr.enabled && xr.isPresenting)) return;

		const camera = this.el.camera.el.object3D;
		const globalOffset = camera.parent!.matrixWorld;

		const frame = xr.getFrame(), refSpace = xr.getReferenceSpace()!, inputs = xr.getSession()!.inputSources;
		for(let n = inputs.length; --n >= 0; )
		{
			const input = inputs[n];
			if( input.hand )
			{
				if(!frame.getJointPose) continue; //wtf.
				const instance = this.instances[ input.handedness + "-hand" ];
				if(!instance) continue;

				instance.input = input;
				const rayJoint = input.hand.get("index-finger-metacarpal" as any);
				if(!rayJoint) continue;
				instance.//updateGesture(input.hand, frame, refSpace).
					updateRay(frame.getJointPose(rayJoint, refSpace), globalOffset);
			}
			else if( input.gamepad && input.gamepad.connected ) //"else" because tracked hands have a 1-button gamepad
			{
				const instance = this.instances[ input.handedness + "-gamepad" ];
				if(!instance) continue; 

				instance.input = input;

				let axes = input.gamepad.axes, buttons = input.gamepad.buttons;

				//Handle the pad for poor, sad htc-vive controllers :
				const padButton = buttons[2];
				if(padButton && (axes.length >= 2))
				{
					//Axes 0 and 1 are "Pad" axes. Ignore them unless "Pad" button is pressed.
					if(!padButton.pressed)
						axes = [0, 0, ...axes.slice(2) ];

					//Only trigger, grip, and pad, maybe stick : simulate a 5th button in the middle of the pad
					if(buttons.length < 5) 
					{
						const padAxisLength = Math.sqrt( axes[0] * axes[0] + axes[1] * axes[1] );
						const centerRadius = 0.333;

						const padAxisCentered = padAxisLength < centerRadius;
						const midPadButton : GamepadButton = padAxisCentered ? padButton : { pressed : false, touched : false, value : 0.0 };
						
						const newButtons = buttons.slice();
						while(newButtons.length < 4)
							newButtons.push( { pressed:false, touched:false, value:0.0 } );
						newButtons.push( midPadButton );
						buttons = newButtons;
						
						//Adjust the axes on the remaining surface :
						const k = padAxisCentered ? 0.0 : ((padAxisLength - centerRadius) / ( padAxisLength * (1.0 - centerRadius) ));
						axes = [ k * axes[0], k * axes[1], ...axes.slice(2) ];
					}
				}


				instance.updateButtons(buttons).updateAxes(axes).
					updateRay(frame.getPose(input.targetRaySpace, refSpace), globalOffset);
			}
		}
	}
});








registerComponent("xr-input", {
	schema : { "" : {type:"string"}  },
	multiple : true,
	init(this : XrInputComponent)
	{
		if(this.id)
			this.system.instances[ this.id ] = this;
		this.buttons = [];
		this.axes = [];
		this.gesture = "";
		this.actions = {};
		this.rayOrigin = new Vector3();
		this.rayDirection = new Vector3();
		this.getRay = (origin, direction, setter) =>
		{
			setter.call(origin, this.rayOrigin.x, this.rayOrigin.y, this.rayOrigin.z);
			setter.call(direction, this.rayDirection.x, this.rayDirection.y, this.rayDirection.z);
		};

		this.el.addEventListener("enter-interactive-range", () => this.feedback(1));
		this.el.addEventListener("exit-interactive-range", () => this.feedback(0.8));
	},
	feedback(this : XrInputComponent, intensity : number, milliseconds: number, action? : string)
	{
		if(this.input && this.input.gamepad && this.input.gamepad.hapticActuators)
		{
			const ha = this.input.gamepad.hapticActuators[0] as ( GamepadHapticActuator & { pulse? : (k:number, d:number) => void });
			ha && ha.pulse && ha.pulse( intensity, isNaN(milliseconds) ? 100 : milliseconds );
		}
				
	},
	updateButtons(this: XrInputComponent, buttons : ReadonlyArray<GamepadButton> ) : XrInputComponent
	{
		const keyMap = this.system.data;


		let thumb = false, index = false, other = false;


		for(let n=buttons.length; --n >= 0; )
		{
			const btn = buttons[n];

			if(btn.pressed || btn.touched || (btn.value > 0))
			{
				if(n == 0) index = true;
				else if(n == 1) other = true;
				else thumb = true;
			}

			const value = btn.pressed ? 1.0 : (btn.touched ? btn.value : -1.0);
			const previous = this.buttons[n] || -1.0;
			
			if(value == previous) continue;
			this.buttons[n] = value;

			const key = buttonName(n);
			let action = keyMap[key];
			if(!action) continue; //No action mapped.

			action = this.data[action] || action; 
			if(value > 0)
				press(this, action, key, value, this.getRay);
			else
				release(this, action, key);
		}



		const indexDetail : SetAnimationEventDetail = 
		{
			level : 1010,
			animations : index ?  {
				".*" : {
					"filter" : "Index1(\/|$)",
					"clips" : [ "HandClosed" ]
				}
			} : {}
		};
		this.el.emit("set-animation", indexDetail);


		const thumbDetail : SetAnimationEventDetail = 
		{
			level : 1020,
			animations : thumb ?  {
				".*" : {
					"filter" : "Thumb1(\/|$)",
					"clips" : [ "ThumbClosed" ]
				}
			} : {}
		};
		this.el.emit("set-animation", thumbDetail);
	

		const otherDetail : SetAnimationEventDetail = 
		{
			level : 1030,
			animations : other ?  {
				".*" : {
					"filter" : "(Middle1|Ring1|Pinky1)(\/|$)",
					"clips" : [ "HandClosed" ]
				}
			} : {}
		};
		this.el.emit("set-animation", otherDetail);
	

		return this;
	},
	updateAxes(this : XrInputComponent, axes : ReadonlyArray<number> ) : XrInputComponent
	{
		const keyMap = this.system.data;
		for(let n=axes.length; --n >= 0; )
		{
			const value = axes[n], previous = this.axes[n] || 0.0;
			
			if(value == previous) continue;
			this.axes[n] = value;

			if((previous != 0) && ((value * previous) <= 0)) //Change of direction or back to zero
			{
				const key = axisName(n, previous);
				const action = keyMap[key];
				action && release(this, this.data[action] || action, key);
			}

			if(value != 0)
			{
				const key = axisName(n, value);
				const action = keyMap[key];
				action && press(this, this.data[action] || action, key, Math.abs(value), this.getRay);
			}
		}
		return this;
	},
	updateGesture(this : XrInputComponent, hand : XRHand, frame : XRFrame, ref : XRReferenceSpace) : XrInputComponent
	{
		/* this sucks 
		let gesture = "";
		for(let finger of ["index", "middle", "ring", "pinky"])
		{
			const aJoint = hand.get( (finger + "-finger-metacarpal") as any );
			if(!aJoint) continue;
			const bJoint = hand.get( (finger + "-finger-phalanx-intermediate") as any );
			if(!bJoint) continue;
			const aPose = frame.getJointPose!(aJoint, ref);
			if(!aPose) continue;
			const bPose = frame.getJointPose!(bJoint, ref);
			if(!bPose) continue;
			const aMat = aPose.transform.matrix, bMat = bPose.transform.matrix;
			const dot = aMat[8]*bMat[8] + aMat[9]*bMat[9] + aMat[10]*bMat[10];
			if(dot > 0.5)
				gesture += finger.slice(0,1).toUpperCase();
			else if(dot < 0.5)
				gesture += finger.slice(0,1).toLowerCase();
		}

		if(gesture != this.gesture)
		{
			// this.el.setAttribute("text", { value : gesture });
			if(this.gesture)
			{
				const action = this.data[this.gesture];
				action && release(this, action, this.gesture);
			}
			if(this.gesture = gesture)
			{
				const action = this.data[this.gesture];
				action && press(this, action, this.gesture, 1.0, this.getRay);
			}
		}
		*/
		return this;
	},
	updateRay(this : XrInputComponent, pose : XRPose | undefined, globalOffset : Matrix4 ) : XrInputComponent
	{
		const cam = this.el.sceneEl!.camera.el.object3D;
		
		if(pose)
		{
			const poseM = pose.transform.matrix;
			
			transformPoint( this.rayOrigin.set(poseM[12], poseM[13], poseM[14]), globalOffset );
			transformVector( this.rayDirection.set(-poseM[8], -poseM[9], -poseM[10]), globalOffset );

			/* if(this.el !== this.el.sceneEl) //on an entity
			{
				this.el.object3D.position.copy(this.rayOrigin);
				this.el.object3D.matrix.fromArray(poseM).premultiply(globalOffset);
				this.el.object3D.quaternion.setFromRotationMatrix(this.el.object3D.matrix);
			} */
		}
		else
		{
			const camM = cam.matrixWorld.elements;
			this.rayOrigin.set(camM[12], camM[13], camM[14]);
			this.rayDirection.set(-camM[8], -camM[9], -camM[10]);
		}
		return this;
	}
});

